import React from 'react';
import PropTypes from 'prop-types';
import {
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import i18n from '../../../i18n';
import ToolbarDropdownButton from '../../../components/toolbar/ToolbarDropdownButton';

/**
 * Holds the toolbar actions that are rarely used or specific to one grid —
 * archiving, locking an instance, moving a balance. Kept out of the toolbar
 * proper so the actions people reach for every day aren't buried among a row of
 * identical icon buttons.
 *
 * Renders nothing when no items are permitted.
 */
const ToolbarOverflowMenu = ({ items }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const visible = items.filter(Boolean);

    if (!visible.length) return null;

    const handleClose = () => setAnchorEl(null);

    return (
        <>
            <Tooltip title={i18n.t('common.moreActions')}>
                <ToolbarDropdownButton
                    iconOnly
                    aria-label={i18n.t('common.moreActions')}
                    icon={<MoreVertIcon />}
                    open={Boolean(anchorEl)}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                />
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {visible.map((item, index) => [
                    item.dividerBefore && index > 0 ? (
                        <Divider key={`${item.key}-divider`} />
                    ) : null,
                    <MenuItem
                        key={item.key}
                        onClick={() => {
                            item.onClick();
                            handleClose();
                        }}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText>{item.label}</ListItemText>
                    </MenuItem>
                ])}
            </Menu>
        </>
    );
};

ToolbarOverflowMenu.propTypes = {
    // An item a grid does not offer is passed as `false` (`perm && {…}`) and
    // filtered out here, so false is a valid entry.
    items: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.oneOf([false, null]),
            PropTypes.shape({
                key: PropTypes.string.isRequired,
                label: PropTypes.node.isRequired,
                icon: PropTypes.node,
                onClick: PropTypes.func.isRequired,
                dividerBefore: PropTypes.bool
            })
        ])
    )
};

ToolbarOverflowMenu.defaultProps = {
    items: []
};

export default ToolbarOverflowMenu;
